import React, {memo, ReactNode, RefObject, useEffect, useLayoutEffect, useRef, useState} from 'react';
import styled from 'styled-components';

import {convertToDarkMode} from '../../../../../../libs/shared/ui/src/styles/darkModeHelpers';
import {fonts} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {expandedDarkTheme} from '../../../../../../libs/shared/ui/src/theme/dark/darkTheme';
import {lightTheme} from '../../../../../../libs/shared/ui/src/theme/light/lightTheme';
import {ThemeTypesEnum} from '../../../../../../libs/shared/ui/src/theme/themeTypes';
import {useUserTheme} from '../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {searchableAttrsBody} from '../../../../../../srcCommon/inboxes/search/searchHelpers';
import {reportToBugsnag} from '../../../../../../srcCommon/services/bugsnag/bugsnag';
import {blockquoteStyle} from '../../../../../../srcCommon/styles/editorStyles';
import {useVariableHeightContext} from '../../../../common/scroll/variableHeightContext';
import {emailCssHacks} from './email/emailCssHacks';

/*
 * Constants.
 */

const CONTENT_CONTAINER_CLASSNAME = 'front-message-html';

/*
 * Props.
 */

interface MessageHtmlProps {
  id?: string;
  html?: string;
  children?: ReactNode;
}

/*
 * Style.
 */

const StyledHtmlDiv = styled.div`
  white-space: initial;
  font-family: ${fonts.system};
  line-height: 1.4;
  color: initial;
  position: relative;
  z-index: 0;

  overflow-wrap: break-word;

  *,
  *::after,
  *::before {
    box-sizing: content-box;
  }

  a,
  a:visited,
  a:active {
    color: ${(p) => p.theme.palette.blue.shade40};
  }

  a,
  a * {
    cursor: pointer;
  }

  a:hover {
    color: ${(p) => p.theme.palette.blue.shade50};
  }

  .front-blockquote {
    margin: 0;
    border-left: 1px solid ${(p) => p.theme.palette.grey.shade30};
    padding-left: 10px;
  }

  blockquote:not([class]):not([style]) {
    ${blockquoteStyle}
  }

  img:not([style]):not([height]) {
    max-width: 100%;
    height: auto;
  }

  /* Override user-agent's default styles to avoid impossible-to-read indentation in long threads when blockquotes don't have the "front-blockquote" class applied to them. */
  blockquote {
    margin-inline-end: 0;
    margin-inline-start: 0.2em;
  }

  /* Apply email/providers specific hacks ( :( ) */
  ${emailCssHacks}
`;

/*
 * Component.
 */

export const MessageHtml = memo<MessageHtmlProps>((props) => {
  const {elementRef} = useImageLoadDetection();
  const isContentDarkMode = useDarkMode(elementRef, props.html);

  // If an HTML content was provided, use it as content.
  if (props.html) {
    return (
      <StyledHtmlDiv
        id={props.id}
        theme={isContentDarkMode ? expandedDarkTheme : lightTheme}
        ref={elementRef}
        dangerouslySetInnerHTML={{__html: wrapHtmlWithDarkModeContainer(props.html)}}
        {...searchableAttrsBody}
      />
    );
  }

  // Otherwise, just use the children.
  return (
    <StyledHtmlDiv id={props.id} ref={elementRef} {...searchableAttrsBody}>
      {props.children}
    </StyledHtmlDiv>
  );
});

function useImageLoadDetection() {
  const elementRef = useRef<HTMLDivElement>(null);
  const {onElementLoaded} = useVariableHeightContext();

  // After each update, find all the images in our content.
  useEffect(() => {
    const currentBody = elementRef.current;
    if (!currentBody) {
      return;
    }

    // Find all the images in our content.
    const imageNodes = Array.from(currentBody.querySelectorAll('img'));
    imageNodes.forEach((imageNode) => {
      // Remove empty alt attributes from inline images from Front, because they break offline image placeholders.
      // TODO: Remove this temporary workaround when enough time has passed.
      if (imageNode.hasAttribute('front-cid') && imageNode.getAttribute('alt') === '') {
        imageNode.removeAttribute('alt');
      }

      // If the image is already loaded, notify now.
      if (imageNode.complete) {
        onElementLoaded();
        return;
      }

      // Otherwise, wait for the image to load.
      const onImageNodeLoaded = () => {
        imageNode.removeEventListener('load', onImageNodeLoaded);
        onElementLoaded();
      };
      imageNode.addEventListener('load', onImageNodeLoaded);
    });
  });

  return {elementRef};
}

// Wrapping the HTML with a div allows us to style top level text nodes
// without modifying the StyledHtmlDiv itself.
function wrapHtmlWithDarkModeContainer(html: string) {
  return `<div class="${CONTENT_CONTAINER_CLASSNAME}">${html}</div>`;
}

function setLightBackgroundForContent(container: HTMLElement) {
  const contentNode = container.querySelector(`.${CONTENT_CONTAINER_CLASSNAME}`);
  if (!contentNode || !(contentNode instanceof HTMLElement)) {
    reportToBugsnag(new Error('Container node not found for message'));
    return;
  }
  contentNode.style.background = lightTheme.foreground;
}

function useDarkMode(ref: RefObject<HTMLElement>, html?: string) {
  const userTheme = useUserTheme();
  const isExpandedDarkTheme = userTheme.type === ThemeTypesEnum.EXPANDED_DARK;
  const [isContentDarkMode, setIsContentDarkMode] = useState(isExpandedDarkTheme);
  const previousHtml = useRef('');

  useLayoutEffect(() => {
    if (!ref.current || !html) {
      previousHtml.current = '';
      return;
    }

    const handleLightModeTheme = (container: HTMLElement) => {
      // If the content has been converted to dark mode, but the user's theme has switched
      // to light mode, rewrite the innerHTML with the original.
      if (isContentDarkMode) {
        // eslint-disable-next-line
        container.innerHTML = wrapHtmlWithDarkModeContainer(html);
        setIsContentDarkMode(false);
      }
    };

    const handleDarkModeTheme = (container: HTMLElement) => {
      // If the html has changed, we're dealing with a fresh DOM subtree. Otherwise,
      // if nothing has changed, use the isContentDarkMode boolean to avoid color inverting
      // the content a second time.
      if (previousHtml.current !== html || !isContentDarkMode) {
        const isConverted = convertToDarkMode(container, userTheme.foreground);
        setIsContentDarkMode(isConverted);
        if (!isConverted) {
          setLightBackgroundForContent(container);
        }
      }
    };

    if (isExpandedDarkTheme) {
      handleDarkModeTheme(ref.current);
    } else {
      handleLightModeTheme(ref.current);
    }

    previousHtml.current = html;
  }, [html, isContentDarkMode, isExpandedDarkTheme, ref, userTheme.foreground]);

  return isContentDarkMode;
}
